import {
	Award,
	Flower2,
	Heart,
	Leaf,
	PackageCheck,
	ShieldCheck,
	Sprout,
	Sun,
	Truck,
	Users,
	type LucideIcon,
} from "lucide-react";
import { BUSINESS } from "./business";
import honeyPage from "../content/honeyPage.json";

/**
 * Testi e immagini del sito modificabili dalla dashboard (tabella
 * site_content, una riga per sezione). I valori qui sotto sono quelli
 * mostrati finché una sezione non viene salvata almeno una volta, e fanno da
 * riserva per ogni campo lasciato vuoto. I testi della home sono ripetuti in
 * scripts/prerender.js: se cambi un default, cambialo anche lì. Le pagine
 * lunghe (come "Il nostro miele") hanno i default in src/content/*.json,
 * letti anche dal prerender.
 */

/** Icone tra cui scegliere per i punti di forza. */
export const FEATURE_ICONS: Record<string, { icon: LucideIcon; label: string }> = {
	ShieldCheck: { icon: ShieldCheck, label: "Scudo" },
	Leaf: { icon: Leaf, label: "Foglia" },
	Users: { icon: Users, label: "Famiglia" },
	PackageCheck: { icon: PackageCheck, label: "Pacco" },
	Flower2: { icon: Flower2, label: "Fiore" },
	Sprout: { icon: Sprout, label: "Germoglio" },
	Sun: { icon: Sun, label: "Sole" },
	Heart: { icon: Heart, label: "Cuore" },
	Award: { icon: Award, label: "Premio" },
	Truck: { icon: Truck, label: "Furgone" },
};

export const featureIcon = (name: string): LucideIcon =>
	FEATURE_ICONS[name]?.icon ?? ShieldCheck;

/** Profili social supportati: ognuno ha colore e icona in SocialButtons. */
export const SOCIAL_NETWORKS = ["Instagram", "Facebook"] as const;
export type SocialNetwork = (typeof SOCIAL_NETWORKS)[number];

export interface SiteContent {
	"home.hero": {
		eyebrow: string;
		title: string;
		text: string;
		ctaLabel: string;
		/** URL nel bucket site-images; null = foto predefinita. */
		image: string | null;
	};
	"home.stats": {
		items: { value: string; label: string }[];
	};
	"home.story": {
		title: string;
		text: string;
		image: string | null;
	};
	"home.features": {
		title: string;
		intro: string;
		items: { icon: string; title: string; description: string }[];
	};
	"home.cta": {
		title: string;
		text: string;
	};
	"page.honey": {
		title: string;
		intro: string;
		image: string | null;
		/** text: HTML dall'editor formattato (o testo semplice, una riga per paragrafo). */
		sections: { title: string; text: string; images: string[] }[];
		faq: { question: string; answer: string }[];
	};
	"page.recipes": {
		title: string;
		intro: string;
	};
	"page.posts": {
		title: string;
		intro: string;
	};
	"site.contacts": {
		email: string;
		phone: string;
		/** URL del profilo per rete; vuoto = pulsante nascosto. */
		social: Record<SocialNetwork, string>;
	};
}

export type SiteContentKey = keyof SiteContent;

export const SITE_CONTENT_DEFAULTS: { [K in SiteContentKey]: SiteContent[K] } = {
	"home.hero": {
		eyebrow: "Miele Puro 100% Grezzo Non Pastorizzato",
		title: "Apicoltura DePiccoli",
		text: "La passione per le api trasformata in miele artigianale di qualità. Raccogliamo con cura e rispetto nel cuore del Veneto, preservando ogni singolo profumo floreale della nostra terra.",
		ctaLabel: "Scopri i Prodotti",
		image: null,
	},
	"home.stats": {
		items: [
			{ value: "100%", label: "Puro Naturale" },
			{ value: "Veneto", label: "Origine Protetta" },
			{ value: "Bio", label: "Certificato Biologico" },
		],
	},
	"home.story": {
		title: "Tutto è nato dalla passione di papà per le api.",
		text: "Posizioniamo i nostri apiari lontano da fonti di inquinamento, fra i prati e le siepi della pianura trevigiana. Preleviamo i melari solo a maturazione completa e lavoriamo ogni raccolto in un laboratorio autorizzato: disopercolato a mano, centrifugato, decantato.",
		image: null,
	},
	"home.features": {
		title: "I Nostri Punti di Forza",
		intro: "Dalla cura quotidiana degli apiari fino al confezionamento manuale in vasetto, ogni passaggio rispetta i tempi lenti della natura.",
		items: [
			{
				icon: "ShieldCheck",
				title: "Qualità Garantita",
				description:
					"Preleviamo i melari solo a maturazione completa, garantendo la giusta umidità naturale inferiore al 17%.",
			},
			{
				icon: "Leaf",
				title: "Sostenibilità",
				description:
					"Posizioniamo i nostri apiari lontano da fonti di inquinamento, fra i prati e le siepi della pianura trevigiana.",
			},
			{
				icon: "Users",
				title: "Progetto Familiare",
				description:
					"Tutto è nato dalla passione di papà per le api. Ogni vasetto passa ancora dalle mani della famiglia.",
			},
			{
				icon: "PackageCheck",
				title: "Imballo Curato",
				description: "Ogni vasetto viene imballato con cura per arrivare integro a casa tua.",
			},
		],
	},
	"home.cta": {
		title: "Vuoi Assaggiare il Nostro Miele?",
		text: "Scopri il sapore autentico del miele artigianale prodotto con passione. Contattaci per assaggiare la qualità della nostra produzione familiare o richiedere una consegna a domicilio.",
	},
	"page.honey": honeyPage as SiteContent["page.honey"],
	"page.recipes": {
		title: "Ricette con il nostro miele",
		intro: "Idee semplici per portare il miele in tavola, dalla colazione ai formaggi.",
	},
	"page.posts": {
		title: "Eventi e novità",
		intro: "Dove trovarci nei prossimi mercatini, cosa succede in apiario e qualche racconto sul mondo delle api.",
	},
	"site.contacts": {
		email: BUSINESS.email,
		phone: BUSINESS.phone ?? "",
		social: {
			Instagram: "https://www.instagram.com/apicolturiamoci?igsh=Z29keGw1cWo4anV1",
			Facebook: "",
		},
	},
};

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
	typeof v === "object" && v !== null && !Array.isArray(v);

/**
 * Sovrappone il contenuto salvato ai default, campo per campo: un campo
 * mancante o di tipo sbagliato torna al default, così una riga incompleta (o
 * di una versione precedente) non lascia buchi nella pagina. Le liste
 * salvate sostituiscono quelle predefinite per intero. I campi obbligatori
 * li controlla la dashboard prima di salvare: una stringa vuota qui è voluta
 * (telefono o social nascosti).
 */
export function mergeContent<T>(defaults: T, saved: unknown): T {
	if (saved === undefined) return defaults;
	// Immagini: null = foto predefinita, altrimenti l'URL caricato.
	if (defaults === null) return (typeof saved === "string" && saved ? saved : null) as T;
	if (saved === null) return defaults;
	if (Array.isArray(defaults)) return (Array.isArray(saved) ? saved : defaults) as T;
	if (isPlainObject(defaults)) {
		if (!isPlainObject(saved)) return defaults;
		const out: Record<string, unknown> = {};
		for (const [k, v] of Object.entries(defaults)) out[k] = mergeContent(v, saved[k]);
		return out as T;
	}
	return (typeof saved === typeof defaults ? saved : defaults) as T;
}
